import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash, Truck, ArrowLeft, MagnifyingGlass, UserPlus, X, Check } from "@phosphor-icons/react";
import { isValidGstin, lookupGstin, normalizeGstin } from "@/lib/gstinLookup";
import { GroupPicker } from "@/components/GroupPicker";
import { Switch } from "@/components/ui/switch";
import { useFieldCfg } from "@/lib/softwareConfig";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Create / Edit Transport Invoice.
 *
 * The same component renders both flows — when the route matches
 * `/transport-invoices/:id/edit` the `id` param triggers a fetch that
 * pre-fills the form; on submit we PUT instead of POST. Bill No is
 * frozen in edit mode (the sequence has already burned that number).
 *
 * The party field is a searchable dropdown backed by /api/customers.
 * A user who can't find the transporter in the list can create one
 * inline via a small modal — the new customer is saved to the master
 * Customers section AND linked to the transport invoice in the same
 * transaction so it shows up everywhere without a second copy-paste.
 */
export default function CreateTransportInvoice() {
  const fcfg = useFieldCfg("transport_invoice");
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);

  const [saving, setSaving] = useState(false);
  const [nextNo, setNextNo] = useState("TRP-00001");
  const [customers, setCustomers] = useState([]);
  const [sellerCode, setSellerCode] = useState("");

  const [form, setForm] = useState({
    invoice_date: new Date().toISOString().slice(0, 10),
    party_name: "",
    party_address: "",
    party_gstin: "",
    party_state: "",
    party_state_code: "",
    customer_id: null,
    gst_type: "cgst_sgst",
    gst_rate: 12,
    rcm: false,
    notes: "",
  });
  const [items, setItems] = useState([
    { cn_no: "", from_location: "", to_location: "", transport_details: "", hsn_code: "996791", amount: 0 },
  ]);

  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [partyGstinMsg, setPartyGstinMsg] = useState(null);

  const onPartyGstinChange = async (raw) => {
    const g = normalizeGstin(raw);
    setForm((f) => ({ ...f, party_gstin: g }));
    if (g.length < 15) { setPartyGstinMsg(null); return; }
    if (!isValidGstin(g)) { setPartyGstinMsg({ tone: "text-rose-600", text: "Invalid GSTIN format" }); return; }
    setPartyGstinMsg({ tone: "text-blue-600", text: "Fetching from GST records…" });
    try {
      const d = await lookupGstin(g);
      if (d.found) {
        const addr = [d.address, d.city, d.pincode ? `- ${d.pincode}` : ""].filter(Boolean).join(", ").replace(", - ", " - ");
        setForm((f) => ({
          ...f,
          party_name: d.name || f.party_name,
          party_address: addr || f.party_address,
          party_state: d.state || f.party_state,
        }));
        setPartyGstinMsg({ tone: "text-emerald-700", text: `✓ ${d.name} — auto-filled` });
      } else {
        setForm((f) => ({ ...f, party_state: d.state || f.party_state }));
        setPartyGstinMsg({ tone: "text-amber-700", text: d.message || "Only state auto-filled" });
      }
    } catch {
      setPartyGstinMsg({ tone: "text-rose-600", text: "Lookup failed" });
    }
  };

  // Load master customer list (used by the party dropdown)
  const loadCustomers = async () => {
    try {
      const r = await axios.get(`${API}/customers`);
      setCustomers(r.data || []);
    } catch (_) { /* non-fatal — user can still type party name freely */ }
  };

  useEffect(() => { loadCustomers(); }, []);
  useEffect(() => {
    axios.get(`${API}/settings`)
      .then(({ data: s }) => setSellerCode(((s?.state_code || (s?.gstin || "").slice(0, 2)) || "").toString()))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isEdit) {
      // Edit mode — hydrate form + items from the existing TI.
      axios.get(`${API}/transport-invoices/${editId}`)
        .then(({ data }) => {
          setForm({
            invoice_date: data.invoice_date,
            party_name: data.party_name || "",
            party_address: data.party_address || "",
            party_gstin: data.party_gstin || "",
            party_state: data.party_state || "",
            party_state_code: data.party_state_code || "",
            customer_id: data.customer_id || null,
            gst_type: data.gst_type || "cgst_sgst",
            gst_rate: data.gst_rate ?? 12,
            rcm: data.rcm || false,
            notes: data.notes || "",
          });
          setItems(data.items && data.items.length ? data.items : items);
          setNextNo(data.invoice_no);
        })
        .catch((e) => {
          if (e?.response?.status === 404) navigate("/transport-invoices", { replace: true });
        });
    } else {
      axios.get(`${API}/transport-invoices/next-number`)
        .then((r) => setNextNo(r.data.invoice_no))
        .catch((e) => {
          if (e?.response?.status === 404) navigate("/dashboard", { replace: true });
        });
    }
    // eslint-disable-next-line
  }, [editId, isEdit]);

  const setItem = (i, patch) =>
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const addRow = () =>
    setItems((prev) => [
      ...prev,
      { cn_no: "", from_location: "", to_location: "", transport_details: "", hsn_code: "996791", amount: 0 },
    ]);
  const removeRow = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  // Populate party fields when the user picks an existing customer.
  // Also auto-derive GST type: same state as company GST registration →
  // CGST+SGST, different state → IGST (unless user chose No GST).
  const pickCustomer = (c) => {
    const buyerCode = ((c.state_code || (c.gstin || "").slice(0, 2)) || "").toString();
    setForm((prev) => {
      let gt = prev.gst_type;
      if (prev.gst_type !== "no_gst" && sellerCode && buyerCode) {
        gt = buyerCode === sellerCode ? "cgst_sgst" : "igst";
      }
      return {
        ...prev,
        customer_id: c.id,
        party_name: c.name || "",
        party_address: c.address || "",
        party_gstin: c.gstin || "",
        party_state: c.state || "",
        party_state_code: c.state_code || "",
        gst_type: gt,
      };
    });
  };

  // Detach the linked customer — used when the user manually edits the
  // party name after picking one from the dropdown.
  const clearCustomerLink = () => {
    setForm((prev) => ({ ...prev, customer_id: null }));
  };

  const subtotal = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
  const gstRate = form.gst_type === "no_gst" ? 0 : (parseFloat(form.gst_rate) || 0);
  const gstAmt = subtotal * gstRate / 100;
  const cgst = form.gst_type === "cgst_sgst" ? gstAmt / 2 : 0;
  const sgst = cgst;
  const igst = form.gst_type === "igst" ? gstAmt : 0;
  const total = form.rcm ? subtotal : subtotal + cgst + sgst + igst;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.party_name.trim() || !form.party_address.trim()) {
      alert("Party name and address are required");
      return;
    }
    if (items.some((it) => !it.cn_no.trim() || !it.from_location.trim() || !it.to_location.trim())) {
      alert("Every row needs a CN No, From location and To location");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        gst_rate: parseFloat(form.gst_rate) || 0,
        items: items.map((it) => ({ ...it, amount: parseFloat(it.amount) || 0 })),
      };
      let resp;
      if (isEdit) {
        resp = await axios.put(`${API}/transport-invoices/${editId}`, payload);
      } else {
        resp = await axios.post(`${API}/transport-invoices`, payload);
      }
      navigate(`/transport-invoices/${resp.data.id}`, { replace: true });
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to save transport invoice");
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid={isEdit ? "edit-ti-page" : "create-ti-page"}>
      <button
        onClick={() => navigate(isEdit ? `/transport-invoices/${editId}` : "/transport-invoices")}
        className="flex items-center gap-1 text-slate-500 hover:text-slate-900 text-sm mb-4"
      ><ArrowLeft size={16} /> Back</button>

      <div className="flex items-center gap-2 mb-1 text-slate-500 text-xs font-bold uppercase tracking-widest">
        <Truck size={14} weight="fill" /> {isEdit ? "Edit Transport Invoice" : "New Transport Invoice"}
      </div>
      <h1 className="text-3xl font-black tracking-tighter text-slate-900 mb-6">
        Bill No: <span className="font-mono text-slate-700">{nextNo}</span>
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Meta */}
        <section className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-4">Invoice Meta</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Date">
              <input type="date" value={form.invoice_date}
                onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                data-testid="ti-date" required className={inp} />
            </Field>
            <Field label={fcfg.label("gst_type", "GST Type")}>
              <select value={form.gst_type}
                onChange={(e) => setForm({ ...form, gst_type: e.target.value })}
                disabled={!fcfg.canEdit("gst_type")}
                data-testid="ti-gst-type" className={inp}>
                <option value="cgst_sgst">CGST + SGST (intra-state)</option>
                <option value="igst">IGST (inter-state)</option>
                <option value="no_gst">No GST (Tax-free bill)</option>
              </select>
            </Field>
            <Field label={fcfg.label("gst_rate", "GST Rate (%)")}>
              <input type="number" step="0.01" min="0" value={form.gst_rate}
                onChange={(e) => setForm({ ...form, gst_rate: e.target.value })}
                disabled={form.gst_type === "no_gst" || !fcfg.canEdit("gst_rate")}
                data-testid="ti-gst-rate"
                className={`${inp} ${form.gst_type === "no_gst" ? "bg-slate-100 text-slate-400" : ""}`} />
            </Field>
          </div>
          <div className="mt-4 flex items-center justify-between p-3 border border-slate-200 rounded-sm bg-slate-50">
            <div>
              <p className="text-sm font-bold text-slate-900">Reverse Charge Mechanism (RCM)</p>
              <p className="text-xs text-slate-500">Tax payable by the buyer/recipient — invoice total = taxable amount</p>
            </div>
            <Switch checked={form.rcm} onCheckedChange={(v) => setForm({ ...form, rcm: v })} disabled={!fcfg.canEdit("rcm")} data-testid="ti-rcm-toggle" />
          </div>
        </section>

        {/* Party */}
        <section className="bg-white border border-slate-200 rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500">Consignor / Party Details</h2>
            <button type="button" onClick={() => setShowAddCustomer(true)}
              data-testid="ti-add-customer-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold tracking-wide">
              <UserPlus size={14} weight="bold" /> Add new customer
            </button>
          </div>
          <CustomerPicker
            customers={customers}
            selectedId={form.customer_id}
            onPick={pickCustomer}
            onClear={clearCustomerLink}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Party / Company Name" span2>
              <input value={form.party_name}
                onChange={(e) => { setForm({ ...form, party_name: e.target.value }); if (form.customer_id) clearCustomerLink(); }}
                data-testid="ti-party-name" required className={inp} />
            </Field>
            <Field label="Full Address" span2>
              <textarea rows={2} value={form.party_address}
                onChange={(e) => { setForm({ ...form, party_address: e.target.value }); if (form.customer_id) clearCustomerLink(); }}
                data-testid="ti-party-address" required className={inp} />
            </Field>
            <Field label="GSTIN">
              <input value={form.party_gstin} maxLength={15}
                onChange={(e) => onPartyGstinChange(e.target.value)}
                data-testid="ti-party-gstin" placeholder="15-digit GSTIN auto-fetches" className={inp} />
              {partyGstinMsg && <p data-testid="ti-party-gstin-status" className={`mt-1 text-[11px] font-semibold ${partyGstinMsg.tone}`}>{partyGstinMsg.text}</p>}
            </Field>
            <Field label="State (optional)">
              <input value={form.party_state}
                onChange={(e) => setForm({ ...form, party_state: e.target.value })}
                data-testid="ti-party-state" placeholder="Maharashtra" className={inp} />
            </Field>
          </div>
        </section>

        {/* Items */}
        <section className="bg-white border border-slate-200 rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500">Line Items</h2>
            <button type="button" onClick={addRow} data-testid="ti-add-row"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm bg-slate-900 text-white text-xs font-bold hover:bg-slate-800">
              <Plus size={14} weight="bold" /> Add Row
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-bold border-b border-slate-200">
                  <th className="p-2 text-left w-10">Sr</th>
                  <th className="p-2 text-left w-24">CN No</th>
                  <th className="p-2 text-left w-32">From</th>
                  <th className="p-2 text-left w-32">To</th>
                  <th className="p-2 text-left">Transport Details</th>
                  <th className="p-2 text-left w-24">HSN</th>
                  <th className="p-2 text-right w-32">Amount (₹)</th>
                  <th className="p-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} data-testid={`ti-item-row-${i}`} className="border-b border-slate-100">
                    <td className="p-2 text-slate-500">{i + 1}</td>
                    <td className="p-1"><input value={it.cn_no} onChange={(e) => setItem(i, { cn_no: e.target.value })} data-testid={`ti-cn-${i}`} required className={inp} /></td>
                    <td className="p-1"><input value={it.from_location} onChange={(e) => setItem(i, { from_location: e.target.value })} data-testid={`ti-from-${i}`} required className={inp} /></td>
                    <td className="p-1"><input value={it.to_location} onChange={(e) => setItem(i, { to_location: e.target.value })} data-testid={`ti-to-${i}`} required className={inp} /></td>
                    <td className="p-1"><input value={it.transport_details} onChange={(e) => setItem(i, { transport_details: e.target.value })} data-testid={`ti-details-${i}`} className={inp} /></td>
                    <td className="p-1"><input value={it.hsn_code} onChange={(e) => setItem(i, { hsn_code: e.target.value })} data-testid={`ti-hsn-${i}`} className={inp} /></td>
                    <td className="p-1"><input type="number" step="0.01" value={it.amount} onChange={(e) => setItem(i, { amount: e.target.value })} data-testid={`ti-amount-${i}`} className={`${inp} text-right`} /></td>
                    <td className="p-1 text-center">
                      {items.length > 1 && (
                        <button type="button" onClick={() => removeRow(i)}
                          data-testid={`ti-remove-${i}`}
                          className="p-1 text-slate-400 hover:text-rose-600"
                        ><Trash size={16} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Totals */}
        <section className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-4">Totals Preview</h2>
          <div className="grid grid-cols-2 gap-3 max-w-sm ml-auto text-sm">
            <div className="text-slate-500">Subtotal</div>
            <div className="text-right font-mono">₹ {subtotal.toFixed(2)}</div>
            {form.gst_type === "no_gst" ? (
              <>
                <div className="text-slate-500 italic">Tax</div>
                <div className="text-right font-mono text-slate-400 italic" data-testid="ti-preview-notax">Not applicable</div>
              </>
            ) : form.gst_type === "igst" ? (
              <>
                <div className="text-slate-500">IGST ({form.gst_rate}%)</div>
                <div className="text-right font-mono" data-testid="ti-preview-igst">₹ {igst.toFixed(2)}</div>
              </>
            ) : (
              <>
                <div className="text-slate-500">CGST ({(form.gst_rate / 2).toFixed(2)}%)</div>
                <div className="text-right font-mono" data-testid="ti-preview-cgst">₹ {cgst.toFixed(2)}</div>
                <div className="text-slate-500">SGST ({(form.gst_rate / 2).toFixed(2)}%)</div>
                <div className="text-right font-mono" data-testid="ti-preview-sgst">₹ {sgst.toFixed(2)}</div>
              </>
            )}
            {form.rcm && form.gst_type !== "no_gst" && (
              <>
                <div className="text-red-700 font-semibold text-xs">Tax payable by recipient (RCM)</div>
                <div className="text-right font-mono text-xs text-red-700" data-testid="ti-preview-rcm-tax">₹ {(cgst + sgst + igst).toFixed(2)}</div>
              </>
            )}
            <div className="col-span-2 border-t border-slate-200 mt-2 pt-2" />
            <div className="text-slate-900 font-bold text-base">{form.rcm ? "GRAND TOTAL (Taxable)" : "GRAND TOTAL"}</div>
            <div className="text-right font-mono font-black text-lg text-slate-900" data-testid="ti-preview-total">₹ {total.toFixed(2)}</div>
          </div>
        </section>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate(isEdit ? `/transport-invoices/${editId}` : "/transport-invoices")}
            className="px-5 py-2.5 rounded-sm border border-slate-300 text-sm font-bold text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button type="submit" disabled={saving}
            data-testid="ti-save-btn"
            className="px-5 py-2.5 rounded-sm bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 disabled:opacity-60">
            {saving ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save Changes" : "Create Transport Invoice")}
          </button>
        </div>
      </form>

      {showAddCustomer && (
        <AddCustomerModal
          onClose={() => setShowAddCustomer(false)}
          onCreated={(newCust) => {
            setCustomers((prev) => [newCust, ...prev]);
            pickCustomer(newCust);
            setShowAddCustomer(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * Searchable dropdown backed by the master customer list. Selecting a
 * customer auto-populates every party field below; typing to detach the
 * link is handled by the parent (`clearCustomerLink`).
 */
function CustomerPicker({ customers, selectedId, onPick, onClear }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers.slice(0, 10);
    return customers
      .filter((c) => (c.name || "").toLowerCase().includes(q) || (c.gstin || "").toLowerCase().includes(q))
      .slice(0, 10);
  }, [query, customers]);

  const selected = customers.find((c) => c.id === selectedId);

  if (selected) {
    return (
      <div className="mb-4 flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-sm">
        <Check size={18} weight="bold" className="text-emerald-700" />
        <div className="flex-1">
          <div className="text-xs text-emerald-700 font-bold uppercase tracking-widest">Linked to master customer</div>
          <div className="text-slate-900 font-semibold">{selected.name}</div>
        </div>
        <button type="button" onClick={onClear} data-testid="ti-clear-customer"
          className="p-1 text-slate-500 hover:text-slate-800" title="Detach">
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="mb-4 relative">
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          data-testid="ti-customer-search"
          placeholder="Search existing customers by name or GSTIN…"
          className={`${inp} pl-9`}
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-sm shadow-lg max-h-60 overflow-y-auto">
          {filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onPick(c); setQuery(""); setOpen(false); }}
              data-testid={`ti-customer-option-${c.id}`}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
            >
              <div className="font-semibold text-slate-900 text-sm">{c.name}</div>
              {c.gstin && <div className="text-[10px] text-slate-500 font-mono tracking-wider">{c.gstin}</div>}
            </button>
          ))}
        </div>
      )}
      {open && query.trim() && filtered.length === 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-sm shadow-lg p-3 text-sm text-slate-500">
          No customer matches. Fill the fields below manually, or use <b>Add new customer</b>.
        </div>
      )}
    </div>
  );
}

/**
 * Modal to create a new customer without leaving the TI form. The new
 * customer is POSTed to `/api/customers` (which persists it in the
 * master Customers section) and then handed back to the parent so it
 * can auto-fill the party fields.
 */
function AddCustomerModal({ onClose, onCreated }) {
  const [saving, setSaving] = useState(false);
  const [c, setC] = useState({
    name: "", address: "", gstin: "", email: "", phone: "", city: "", state: "", state_code: "", pincode: "", group_id: "",
  });
  const [gstinMsg, setGstinMsg] = useState(null);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    axios.get(`${API}/account-groups`).then((r) => setGroups(r.data.groups || [])).catch(() => {});
  }, []);

  const onGstinChange = async (raw) => {
    const g = normalizeGstin(raw);
    setC((f) => ({ ...f, gstin: g }));
    if (g.length < 15) { setGstinMsg(null); return; }
    if (!isValidGstin(g)) { setGstinMsg({ tone: "text-rose-600", text: "Invalid GSTIN format" }); return; }
    setGstinMsg({ tone: "text-blue-600", text: "Fetching from GST records…" });
    try {
      const d = await lookupGstin(g);
      if (d.found) {
        setC((f) => ({
          ...f,
          name: d.name || f.name,
          address: d.address || f.address,
          city: d.city || f.city,
          state: d.state || f.state,
          state_code: d.state_code || f.state_code,
          pincode: d.pincode || f.pincode,
          phone: d.phone || f.phone,
          email: d.email || f.email,
        }));
        setGstinMsg({ tone: "text-emerald-700", text: `✓ ${d.name}${d.status ? ` (${d.status})` : ""} — auto-filled` });
      } else {
        setC((f) => ({ ...f, state: d.state || f.state, state_code: d.state_code || f.state_code }));
        setGstinMsg({ tone: "text-amber-700", text: d.message || "Only state auto-filled" });
      }
    } catch {
      setGstinMsg({ tone: "text-rose-600", text: "Lookup failed" });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!c.name.trim()) { alert("Name is required"); return; }
    if (!c.group_id) { alert("Select a ledger group in the 'Under' field"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/customers`, c);
      onCreated(data);
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to create customer");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="ti-add-customer-modal">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Master Customer</div>
            <h3 className="text-lg font-bold text-slate-900">Add new customer</h3>
          </div>
          <button onClick={onClose} className="p-1 text-slate-500 hover:text-slate-900"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <p className="text-xs text-slate-500 -mt-2">
            Saved to your main Customers section — you&apos;ll be able to reuse this record from any invoice.
          </p>
          <Field label="Name *">
            <input value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })}
              data-testid="ti-new-customer-name" required className={inp} />
          </Field>
          <Field label="Under (Ledger Group) *">
            <GroupPicker
              groups={groups}
              value={c.group_id}
              onChange={(g) => setC((f) => ({ ...f, group_id: g.id }))}
              onGroupCreated={(g) => setGroups((gs) => [...gs, g].sort((a, b) => a.name.localeCompare(b.name)))}
              testid="ti-new-customer-group-picker"
            />
          </Field>
          <Field label="Address">
            <textarea rows={2} value={c.address} onChange={(e) => setC({ ...c, address: e.target.value })}
              data-testid="ti-new-customer-address" className={inp} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="GSTIN">
              <input value={c.gstin} onChange={(e) => onGstinChange(e.target.value)} maxLength={15}
                placeholder="15-digit GSTIN auto-fetches"
                data-testid="ti-new-customer-gstin" className={inp} />
              {gstinMsg && <p data-testid="ti-gstin-status" className={`mt-1 text-[11px] font-semibold ${gstinMsg.tone}`}>{gstinMsg.text}</p>}
            </Field>
            <Field label="Phone">
              <input value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })}
                data-testid="ti-new-customer-phone" className={inp} />
            </Field>
            <Field label="Email">
              <input type="email" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })}
                data-testid="ti-new-customer-email" className={inp} />
            </Field>
            <Field label="City">
              <input value={c.city} onChange={(e) => setC({ ...c, city: e.target.value })}
                data-testid="ti-new-customer-city" className={inp} />
            </Field>
            <Field label="State">
              <input value={c.state} onChange={(e) => setC({ ...c, state: e.target.value })}
                data-testid="ti-new-customer-state" className={inp} />
            </Field>
            <Field label="Pincode">
              <input value={c.pincode} onChange={(e) => setC({ ...c, pincode: e.target.value })}
                data-testid="ti-new-customer-pincode" inputMode="numeric" maxLength={6} className={inp} />
            </Field>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-sm border border-slate-300 text-sm font-bold text-slate-700 hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              data-testid="ti-save-new-customer"
              className="px-4 py-2 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-60">
              {saving ? "Saving…" : "Save & use"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inp = "w-full px-2.5 py-1.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-slate-900";

function Field({ label, children, span2 }) {
  return (
    <div className={span2 ? "sm:col-span-2" : ""}>
      <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
